import { probeBlender, sendToBlender } from 'Pods/ThreeDStudio/Services/dccBridge/blender'
import { requestDccExportPayload } from 'Pods/ThreeDStudio/Services/dccBridge/requestDccExportPayload'
import { recordDccSendFor } from 'Pods/ThreeDStudio/Services/dccBridgeFirstUse'

import { threeDStudioStore } from '../index'
import { DCC_SEND_STATUS } from '../slices/dccBridge'

let activePluginHandle = null
let activeCallId = 0

// BE returns 202 + { status: 'pending' } while the fbx conversion is still
// running. Auto-FBX runs on run success so this is the rare case; we still
// poll a small number of times before surfacing an error.
const PENDING_POLL_INTERVAL_MS = 1500
const PENDING_POLL_MAX_ATTEMPTS = 30

const STATUSES_PROVING_CONNECTION = new Set([
  DCC_SEND_STATUS.CONNECTING,
  DCC_SEND_STATUS.SENDING,
  DCC_SEND_STATUS.SUCCESS,
  DCC_SEND_STATUS.DCC_BUSY,
])

// Each DCC plugin gets its own client under Services/dccBridge/<dcc>/.
// Routing is intentionally an explicit `if` rather than a registry — when the
// second DCC ships and we know its actual protocol, the right shape for
// shared abstractions will be obvious. Until then, more structure than this
// would just be guesswork.
const probeForSoftware = (softwareName, onResult) => {
  if (softwareName === 'Blender') return probeBlender(onResult)
  return null
}

const sendForSoftware = (softwareName, args) => {
  if (softwareName === 'Blender') return sendToBlender(args)
  return null
}

const wait = (ms) => new Promise((resolve) => { setTimeout(resolve, ms) })

// Polling is intentionally sequential: spacing the BE calls is the whole point.
/* eslint-disable no-await-in-loop */
const fetchExportPayloadWithPolling = async (assetId, isStillActive) => {
  for (let attempt = 0; attempt < PENDING_POLL_MAX_ATTEMPTS; attempt += 1) {
    const result = await requestDccExportPayload(assetId)
    if (result.status === 'ready') return result
    if (!isStillActive()) return null
    await wait(PENDING_POLL_INTERVAL_MS)
    if (!isStillActive()) return null
  }
  return null
}
/* eslint-enable no-await-in-loop */

/**
 * Shallow-merges the given patch into the `dccBridge` slice.
 *
 * @param {Partial<import('../slices/dccBridge').DccBridgeState>} patch
 */
export const setDccBridgeState = (patch) => {
  threeDStudioStore.setState((state) => ({
    dccBridge: { ...state.dccBridge, ...patch },
  }))
}

/**
 * Persists which DCC software the user has selected from the dropdown.
 *
 * @param {string} name - The software identifier (e.g. "Blender").
 */
export const selectDccSoftware = (name) => {
  setDccBridgeState({ selectedSoftwareName: name })
}

/**
 * Records whether the local plugin for `softwareName` is currently reachable.
 * Only writes when the stored value actually changes — strict equality so the
 * very first `false` after `undefined` still lands in the store (downstream
 * polling logic relies on a literal `false` to stop re-probing).
 *
 * @param {string} softwareName
 * @param {boolean} isConnected
 */
export const setSoftwareConnected = (softwareName, isConnected) => {
  if (!softwareName) return
  const current = threeDStudioStore.getState().dccBridge.connectedSoftwares
  if (current[softwareName] === isConnected) return
  setDccBridgeState({
    connectedSoftwares: { ...current, [softwareName]: isConnected },
  })
}

/**
 * Probes the local plugin port without triggering an import and reflects the
 * result on the dccBridge slice. Safe to call repeatedly (e.g. on every
 * popover mount).
 *
 * @param {string} softwareName
 */
export const probeDccConnectivity = (softwareName) => {
  if (!softwareName) return
  probeForSoftware(softwareName, (isConnected) => setSoftwareConnected(softwareName, isConnected))
}

/**
 * Opens the "Send to DCC" instructions modal and records a first-use event for
 * the currently selected software, so dismissing the modal without sending
 * doesn't keep re-triggering it on every subsequent click. After the rolling
 * first-use window expires, the modal will be shown again.
 */
export const openSendToModal = () => {
  const { dccBridge } = threeDStudioStore.getState()
  if (dccBridge.selectedSoftwareName) {
    recordDccSendFor(dccBridge.selectedSoftwareName)
  }
  setDccBridgeState({ isSendModalOpen: true })
}

/**
 * Closes the "Send to DCC" instructions modal.
 */
export const closeSendToModal = () => setDccBridgeState({ isSendModalOpen: false })

/**
 * Clears any visible send-to-DCC notification by resetting status to IDLE and
 * dropping the active target software.
 */
export const dismissDccNotification = () => {
  setDccBridgeState({ sendStatus: DCC_SEND_STATUS.IDLE, sendingTo: null })
}

/**
 * Sends the currently opened model to the given DCC software:
 *   1. Asks the BE for the export payload `{ files, provider }` for the active asset.
 *      Polls while the fbx conversion is still running (BE returns 202 then).
 *   2. Hands files + provider to the locally-running DCC plugin via WebSocket.
 *   3. Reflects each plugin status update on the dccBridge slice so the toast
 *      can render CONNECTING / SENDING / SUCCESS / DCC_BUSY / PLUGIN_NOT_RUNNING / ERROR.
 *
 * @param {string} softwareName - The DCC software to send to.
 * @returns {Promise<void>}
 */
export const sendModelToDcc = async (softwareName) => {
  if (!softwareName) return

  recordDccSendFor(softwareName)

  const opened = threeDStudioStore.getState().ui?.openedModel
  const assetId = opened?.outputAssets?.[0]?.assetId

  if (!assetId) {
    setDccBridgeState({ sendingTo: softwareName, sendStatus: DCC_SEND_STATUS.ERROR })
    return
  }

  // Tear down any in-flight transfer before starting a new one - otherwise the
  // previous WebSocket stays open and the plugin may treat us as still busy.
  activePluginHandle?.close()
  activePluginHandle = null

  // Tag this call so a second invocation that interleaves during the
  // `await requestDccExportPayload` below can be detected and skipped, preventing
  // an orphaned WebSocket when two sends to the same software overlap.
  activeCallId += 1
  const myCallId = activeCallId
  const isStillActive = () =>
    myCallId === activeCallId &&
    threeDStudioStore.getState().dccBridge.sendingTo === softwareName

  setDccBridgeState({
    isSendModalOpen: false,
    sendingTo: softwareName,
    sendStatus: DCC_SEND_STATUS.CONNECTING,
  })

  let payload
  try {
    payload = await fetchExportPayloadWithPolling(assetId, isStillActive)
  } catch {
    setDccBridgeState({ sendStatus: DCC_SEND_STATUS.ERROR })
    return
  }

  if (!payload || !payload.files?.length) {
    setDccBridgeState({ sendStatus: DCC_SEND_STATUS.ERROR })
    return
  }

  if (!isStillActive()) return

  const handle = sendForSoftware(softwareName, {
    files: payload.files,
    name: payload.name,
    provider: payload.provider,
    onStatus: (status) => {
      if (status === DCC_SEND_STATUS.PLUGIN_NOT_RUNNING) {
        setSoftwareConnected(softwareName, false)
      } else if (STATUSES_PROVING_CONNECTION.has(status)) {
        setSoftwareConnected(softwareName, true)
      }
      if (threeDStudioStore.getState().dccBridge.sendingTo !== softwareName) return
      setDccBridgeState({ sendStatus: status })
    },
  })

  if (!handle) {
    setDccBridgeState({ sendStatus: DCC_SEND_STATUS.ERROR })
    return
  }
  activePluginHandle = handle
}
